import React from 'react';
import { useShowMessage } from '../../util/hooks';

/** utility types we use */
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

// these are the props to be injected by the HOC
export interface ShowMessageProps {
  showMessage: ReturnType<typeof useShowMessage>;
}
// P is the props of the wrapped component that is inferred
// C is the actual interface of the wrapped component (used to grab defaultProps fro m it)
export function withShowMessage<
  P extends ShowMessageProps = ShowMessageProps,
  C = React.ComponentType<any>
>(
  // this type allows us to infer P, but grab the type of WrappedComponent separately without it interfering with the inference of P
  WrappedComponent:
    | React.ComponentType<P>
    | (React.JSXElementConstructor<P> & C)
) {
  // the magic is here: JSX.LibraryManagedAttributes will take the type of WrapedComponent and resolve its default props
  // against the props of WithData, which is just the original P type with 'data' removed from its requirements
  type Props = JSX.LibraryManagedAttributes<C, Omit<P, keyof ShowMessageProps>>;

  const displayName =
    (WrappedComponent as any).displayName ||
    WrappedComponent.name ||
    'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithWindowSize = (props: Props) => {
    const showMessage = useShowMessage();
    return <WrappedComponent showMessage={showMessage} {...(props as any)} />;
  };

  ComponentWithWindowSize.displayName = `withShowMessage(${displayName})`;

  return ComponentWithWindowSize;
}
